import { useCallback } from 'react';
import * as yup from 'yup';
import { RESET_PASSWORD_TOKEN_ERROR_CODE, useResetPassword } from 'api';
import useAuthForm from 'ui/hooks/useAuthForm';
import { passwordSchema } from 'utils/schemas';
import { GENERIC_ERROR_MESSAGE, TOKEN_ERROR_MESSAGE } from './constants';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export interface UseResetPasswordFormConfig {
  onSubmit?: () => void;
  onSuccess?: () => void;
  resetPasswordToken?: string;
}

type UseResetPasswordFormResult = Pick<
  ReturnType<typeof useAuthForm<ResetPasswordFormData>>,
  'formState' | 'register' | 'validationState'
> & {
  hasInputError: boolean;
  isLoading: boolean;
  onSubmit: (e: React.SyntheticEvent) => Promise<void>;
};

const schema = yup.object().shape({
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Password confirmation is required'),
  password: passwordSchema.required(),
});

export default function useResetPasswordForm({
  onSubmit,
  onSuccess,
  resetPasswordToken,
}: UseResetPasswordFormConfig): UseResetPasswordFormResult {
  const { mutate, isLoading } = useResetPassword();

  const {
    clearErrors,
    formState,
    handleSubmit,
    hasInputError,
    register,
    setError,
    validationState,
  } = useAuthForm<ResetPasswordFormData>({
    schema,
    defaultValues: {
      confirmPassword: '',
      password: '',
    },
  });

  const submitForm = useCallback(
    async (data: Omit<ResetPasswordFormData, 'unregisteredError'>) => {
      const { password, confirmPassword } = data;

      onSubmit?.();

      clearErrors('unregisteredError');

      mutate(
        { confirmPassword, password, resetPasswordToken },
        {
          onSuccess,
          onError(err) {
            const message =
              err.response?.data?.code === RESET_PASSWORD_TOKEN_ERROR_CODE
                ? TOKEN_ERROR_MESSAGE
                : GENERIC_ERROR_MESSAGE;

            setError('unregisteredError', { message }, { shouldFocus: false });
          },
        },
      );
    },
    [clearErrors, mutate, onSubmit, onSuccess, resetPasswordToken, setError],
  );

  return {
    formState,
    hasInputError,
    register,
    isLoading,
    onSubmit: handleSubmit(submitForm),
    validationState,
  };
}
